import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import pick from 'lodash-es/pick';
import { useUpdateProductItemQuantities } from '../../../bff-api/baskets/useUpdateProductItemQuantities';
import { BffQueryKey } from '../../../bff-api/model/BffQueryKey';
import { useGTM } from '../../../hooks';
import { GTMAddToBagEvent } from '../../../hooks/gtm/model/events/GTMAddToBagEvent';
import { GTMRemoveFromBagEvent } from '../../../hooks/gtm/model/events/GTMRemoveFromBagEvent';
import { getAssociatedExtendProductItem } from '../../../utils/product-utils';
import { useBasketProductState } from '../hooks/useBasketProductState';
import { useCartState } from './useCartState';
import type {
    ProductItem,
    ProductItemEditor,
    UpdateCartProductItem,
} from '../../../bff-api/baskets/model';

export interface UseUpdateCartItemsReturn {
    updateProductItemRemote: (productItemEditor: ProductItemEditor) => void;
    updateProductItemLocally: (productItemEditor: ProductItemEditor) => void;
}

export function useUpdateCartItems(): UseUpdateCartItemsReturn {
    const queryClient = useQueryClient();
    const updateProductItemQuantities = useUpdateProductItemQuantities();
    const { editorProductItems, setEditorProductItems } = useCartState();
    const { getFlagsForProduct, getStateForProduct } = useBasketProductState();
    const { onAddToBag, onRemoveProductFromBag } = useGTM();

    const updateProductItemLocally = useCallback(
        ({ itemId, quantityEditor }: ProductItemEditor) => {
            setEditorProductItems((prev) =>
                prev.map((pie) => {
                    if (pie.itemId === itemId) {
                        return {
                            ...pie,
                            quantityEditor: quantityEditor ?? pie.quantityEditor,
                        };
                    }
                    return pie;
                }),
            );
        },
        [setEditorProductItems],
    );

    const updateProductItemRemote = useCallback(
        (productItemEditor: ProductItemEditor): void => {
            setEditorProductItems((prev) =>
                prev.map((pi) =>
                    pi.itemId === productItemEditor.itemId
                        ? { ...pi, quantityEditor: productItemEditor.quantityEditor }
                        : pi,
                ),
            );
            const { hasEnoughStock } = getFlagsForProduct(
                productItemEditor.productId,
                productItemEditor.quantityEditor,
            ) ?? {
                hasEnoughStock: true,
            };

            if (hasEnoughStock) {
                const onAddToBagItems: ProductItem[] = [];
                const onRemoveProductFromBagItems: ProductItem[] = [];

                if (productItemEditor.quantityEditor > productItemEditor.quantity) {
                    onAddToBagItems.push(productItemEditor);
                } else {
                    onRemoveProductFromBagItems.push(productItemEditor);
                }

                const itemsToUpdate: UpdateCartProductItem[] = [
                    {
                        ...pick(productItemEditor, [
                            'productId',
                            'itemId',
                            'c_extendPlan',
                            'c_ExtendAssociation',
                        ]),
                        quantity: productItemEditor.quantityEditor,
                    },
                ];

                const associatedExtendProductItem = getAssociatedExtendProductItem(
                    productItemEditor,
                    editorProductItems,
                );

                if (
                    associatedExtendProductItem &&
                    associatedExtendProductItem.quantityEditor > productItemEditor.quantityEditor
                ) {
                    onRemoveProductFromBagItems.push(associatedExtendProductItem);

                    itemsToUpdate.push({
                        ...pick(associatedExtendProductItem, [
                            'productId',
                            'itemId',
                            'c_extendPlan',
                            'c_ExtendAssociation',
                        ]),
                        quantity: productItemEditor.quantityEditor,
                    });
                }

                updateProductItemQuantities.mutate(itemsToUpdate, {
                    onSuccess: (payload) => {
                        // the response already returns the basket, so we don't need to refetch it
                        // placing it here will only fire the last mutation, the previous ones will be ignored
                        queryClient.setQueryData([BffQueryKey.BASKET], payload);

                        // we need to update the editor quantity if the associated extend item had to forcefully be
                        if (associatedExtendProductItem) {
                            const newEditorQuantity = payload.productItems?.find(
                                (pi) => pi.itemId === associatedExtendProductItem.itemId,
                            )?.quantity;

                            updateProductItemLocally({
                                ...associatedExtendProductItem,
                                quantityEditor:
                                    newEditorQuantity ?? associatedExtendProductItem.quantity,
                            });
                        }

                        onAddToBagItems.forEach((item) =>
                            onAddToBag(
                                new GTMAddToBagEvent(
                                    'BAG',
                                    payload,
                                    getStateForProduct(item.productId)!.product!,
                                ),
                            ),
                        );
                        onRemoveProductFromBagItems.forEach((item) =>
                            onRemoveProductFromBag(new GTMRemoveFromBagEvent('BAG', payload, item)),
                        );
                    },
                });
            }
        },
        [
            setEditorProductItems,
            getFlagsForProduct,
            updateProductItemLocally,
            queryClient,
            updateProductItemQuantities,
            editorProductItems,
            onAddToBag,
            onRemoveProductFromBag,
            getStateForProduct,
        ],
    );

    return useMemo(
        () => ({
            updateProductItemLocally,
            updateProductItemRemote,
        }),
        [updateProductItemLocally, updateProductItemRemote],
    );
}
